import React, { useState, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faVideo,
  faFileLines,
  faBook,
  faGraduationCap,
  faDiagramProject,
  faTimes,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../../contexts/UserContext";
import apiService from "../../../ApiService";
import "./SourceManager.css";

const SourceManager = () => {
  const { user } = useContext(UserContext);
  const userId = user?.userId;
  const [sources, setSources] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [formData, setFormData] = useState({
    subject_id: "",
    source_name: "",
    source_type: "article",
    source_url: "",
  });

  const sourceTypes = [
    { value: "youtube", label: "YouTube", icon: faVideo, color: "#FF0000" },
    { value: "article", label: "Article", icon: faFileLines, color: "#4facfe" },
    { value: "book", label: "Book", icon: faBook, color: "#43e97b" },
    {
      value: "course",
      label: "Course",
      icon: faGraduationCap,
      color: "#fa709a",
    },
    {
      value: "project",
      label: "Project",
      icon: faDiagramProject,
      color: "#667eea",
    },
  ];

  useEffect(() => {
    fetchSubjects();
    fetchSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchSubjects = async () => {
    try {
      const response = await apiService.getSubjects(userId);
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchSources = async () => {
    setLoading(true);
    try {
      const response = await apiService.getSources(userId, {
        search: searchTerm || undefined,
        source_type: filterType || undefined,
      });
      console.log(
        "Full source object:",
        JSON.stringify(response.sources?.[0], null, 2),
      );
      console.log("Source keys:", Object.keys(response.sources?.[0] || {}));
      setSources(response.sources || []);
    } catch (error) {
      console.error("Error fetching sources:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSources();
  };

  const handleFilterChange = (type) => {
    setFilterType(type === filterType ? "" : type);
  };

  useEffect(() => {
    fetchSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const openCreateModal = () => {
    setEditingSource(null);
    setFormData({
      subject_id: subjects.length > 0 ? subjects[0].subject_id : "",
      source_name: "",
      source_type: "article",
      source_url: "",
    });
    setShowModal(true);
  };

  const openEditModal = (source) => {
    setEditingSource(source);
    setFormData({
      subject_id: source.subject_id,
      source_name: source.source_title || source.source_name,
      source_type: source.source_type,
      source_url: source.source_url || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSource(null);
    setFormData({
      subject_id: "",
      source_name: "",
      source_type: "article",
      source_url: "",
    });
  };

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Map source_name to source_title for API
      const apiData = {
        user_id: userId,
        subject_id: formData.subject_id,
        source_title: formData.source_name,
        source_type: formData.source_type,
        source_url: formData.source_url || null,
      };

      console.log("Submitting source with data:", apiData);

      if (editingSource) {
        await apiService.updateSource(userId, editingSource.source_id, apiData);
      } else {
        await apiService.createSource(userId, apiData);
      }
      closeModal();
      fetchSources();
    } catch (error) {
      console.error("Error saving source:", error);
      alert("Failed to save source. Please try again.");
    }
  };

  const handleDelete = async (sourceId, sourceName) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${sourceName}"? This will also delete all related notes and study logs.`,
      )
    ) {
      try {
        await apiService.deleteSource(userId, sourceId);
        fetchSources();
      } catch (error) {
        console.error("Error deleting source:", error);
        alert("Failed to delete source. Please try again.");
      }
    }
  };

  const getTypeConfig = (type) => {
    return sourceTypes.find((t) => t.value === type) || sourceTypes[0];
  };

  return (
    <div className="source-manager">
      {/* Header Actions */}
      <div className="manager-header">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search sources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button type="submit" className="search-btn">
            Search
          </button>
        </form>
        <button onClick={openCreateModal} className="create-btn">
          <FontAwesomeIcon icon={faPlus} /> Add Source
        </button>
      </div>

      {/* Type Filters */}
      <div className="type-filters">
        {sourceTypes.map((type) => (
          <button
            key={type.value}
            onClick={() => handleFilterChange(type.value)}
            className={`filter-btn ${filterType === type.value ? "active" : ""}`}
            style={{
              backgroundColor:
                filterType === type.value
                  ? type.color
                  : "rgba(255, 255, 255, 0.9)",
              color: filterType === type.value ? "white" : "#4a5568",
            }}
          >
            <FontAwesomeIcon icon={type.icon} />
            <span>{type.label}</span>
          </button>
        ))}
      </div>

      {/* Sources Grid */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading sources...</p>
        </div>
      ) : sources.length === 0 ? (
        <div className="empty-sources">
          <div className="empty-icon">📖</div>
          <h3>No sources found</h3>
          <p>Add learning sources to track your study materials!</p>
          <button onClick={openCreateModal} className="create-btn">
            <FontAwesomeIcon icon={faPlus} /> Add Source
          </button>
        </div>
      ) : (
        <div className="sources-grid">
          {sources.map((source) => {
            const typeConfig = getTypeConfig(source.source_type);
            return (
              <div key={source.source_id} className="source-card">
                <div
                  className="card-type-badge"
                  style={{ background: typeConfig.color }}
                >
                  <FontAwesomeIcon icon={typeConfig.icon} />
                  <span>{typeConfig.label}</span>
                </div>
                <div className="card-header">
                  <h3>{source.source_title || source.source_name}</h3>
                  <div className="card-actions">
                    <button
                      onClick={() => openEditModal(source)}
                      className="action-btn edit-btn"
                      title="Edit"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      onClick={() =>
                        handleDelete(
                          source.source_id,
                          source.source_title || source.source_name,
                        )
                      }
                      className="action-btn delete-btn"
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
                <div className="card-subject">
                  <strong>Subject:</strong> {source.subject_name}
                </div>
                {source.description && (
                  <p className="card-description">{source.description}</p>
                )}
                {source.source_url && (
                  <a
                    href={source.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card-link"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    Open Source
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal &&
        ReactDOM.createPortal(
          <div className="modal-overlay" onMouseDown={handleOverlayMouseDown}>
            <div
              className="modal-content"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingSource ? "Edit Source" : "Add New Source"}</h2>
                <button onClick={closeModal} className="close-btn">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label htmlFor="subject_id">
                    Subject <span className="required">*</span>
                  </label>
                  <select
                    id="subject_id"
                    value={formData.subject_id}
                    onChange={(e) =>
                      setFormData({ ...formData, subject_id: e.target.value })
                    }
                    required
                  >
                    {subjects.length === 0 ? (
                      <option value="">No subjects available</option>
                    ) : (
                      subjects.map((subject) => (
                        <option
                          key={subject.subject_id}
                          value={subject.subject_id}
                        >
                          {subject.subject_name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="source_name">
                    Source Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="source_name"
                    value={formData.source_name}
                    onChange={(e) =>
                      setFormData({ ...formData, source_name: e.target.value })
                    }
                    required
                    maxLength="200"
                    placeholder="e.g., React Official Documentation"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="source_type">
                    Source Type <span className="required">*</span>
                  </label>
                  <select
                    id="source_type"
                    value={formData.source_type}
                    onChange={(e) =>
                      setFormData({ ...formData, source_type: e.target.value })
                    }
                    required
                  >
                    {sourceTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="source_url">Source URL</label>
                  <input
                    type="url"
                    id="source_url"
                    value={formData.source_url}
                    onChange={(e) =>
                      setFormData({ ...formData, source_url: e.target.value })
                    }
                    placeholder="https://..."
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={subjects.length === 0}
                  >
                    {editingSource ? "Update" : "Add"} Source
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SourceManager;
